import { tz } from "@date-fns/tz";
import { add, compareAsc, endOfDay, startOfDay, startOfMonth, sub } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useDRPContext } from "../../context";
import type { IDateRange } from "../../types";
import { useDRDPContext } from "./context";
import type { IYearRange } from "./types";

const initialYearRange: IYearRange = {
  startDate: startOfMonth(new Date(new Date().getFullYear(), new Date().getMonth())),
  endDate: startOfMonth(new Date(new Date().getFullYear(), new Date().getMonth() + 1)),
};
const getMonthYearRange = (range: IDateRange, yearRange: IYearRange = initialYearRange): IYearRange => {
  const nYearRange = { ...yearRange };

  if (range.startDate) {
    nYearRange.startDate = startOfMonth(range.startDate);
  }

  if (range.endDate) {
    nYearRange.endDate = startOfMonth(range.endDate);
  }

  if (nYearRange.endDate <= nYearRange.startDate) {
    nYearRange.endDate = add(nYearRange.startDate, { months: 1 });
  }

  return nYearRange;
};

export const useDRDP = () => {
  const { range, handleRangeChange, timeZone } = useDRPContext();
  const [monthYearRange, setMonthYearRange] = useState<IYearRange>(getMonthYearRange(range));
  const [selectRange, setSelectRange] = useState<IDateRange>(range);
  const [lock, setLock] = useState(true);

  const handleMouseEnter = useCallback(
    (date: Date) => {
      setSelectRange((selectRange) => {
        if (selectRange.startDate !== null && !lock && selectRange.startDate.getTime() <= date.getTime()) {
          return { ...selectRange, endDate: date };
        }
        return selectRange;
      });
    },
    [lock]
  );

  const handleMouseLeave = useCallback(() => {
    if (!lock) {
      setSelectRange((selectRange) => {
        return { ...selectRange, endDate: null };
      });
    }
  }, [lock]);

  const handleSelect = useCallback(
    (date: Date, endDateDisabled?: boolean) => {
      setSelectRange((selectRange) => {
        if (selectRange.startDate === null || lock) {
          const range = { startDate: startOfDay(date, { in: tz(timeZone) }), endDate: null };
          setLock(false);
          handleRangeChange(range);
          return range;
        }
        if (
          selectRange.startDate !== null &&
          !lock &&
          selectRange.startDate.getTime() <= date.getTime() &&
          !endDateDisabled
        ) {
          const range = {
            startDate: startOfDay(selectRange.startDate, { in: tz(timeZone) }),
            endDate: endOfDay(date, { in: tz(timeZone) }),
          };
          setLock(true);
          handleRangeChange(range);
          return range;
        }
        if (selectRange.startDate !== null && !lock && !endDateDisabled) {
          const range = { startDate: startOfDay(date, { in: tz(timeZone) }), endDate: null };
          setLock(false);
          handleRangeChange(range);
          return range;
        }
        if (selectRange.startDate !== null && endDateDisabled) {
          const range = { startDate: startOfDay(date, { in: tz(timeZone) }), endDate: null };
          setLock(true);
          handleRangeChange(range);
          return range;
        }
        return selectRange;
      });
    },
    [handleRangeChange, lock, timeZone]
  );

  const isSelected = useCallback(
    (date: Date): "start" | "end" | "both" | "false" => {
      const fmt = new Intl.DateTimeFormat("en-CA", { timeZone });
      const dateStr = fmt.format(date);
      const startStr = selectRange.startDate !== null ? fmt.format(selectRange.startDate) : null;
      const endStr = selectRange.endDate !== null ? fmt.format(selectRange.endDate) : null;
      if (startStr !== null && startStr === dateStr && endStr !== null && endStr === dateStr) {
        return "both";
      }
      if (startStr !== null && startStr === dateStr) {
        return "start";
      }
      if (endStr !== null && endStr === dateStr) {
        return "end";
      }
      return "false";
    },
    [selectRange, timeZone]
  );

  const isBetween = useCallback(
    (date: Date): boolean => {
      if (selectRange.startDate !== null && selectRange.endDate !== null) {
        const fmt = new Intl.DateTimeFormat("en-CA", { timeZone });
        const dateStr = fmt.format(date);
        const startStr = fmt.format(selectRange.startDate);
        const endStr = fmt.format(selectRange.endDate);
        return dateStr > startStr && dateStr < endStr;
      }
      return false;
    },
    [selectRange, timeZone]
  );

  useEffect(() => {
    setSelectRange((selectedRange) => {
      if (selectedRange !== range) {
        setLock(!(range.startDate && !range.endDate));
      }
      return range;
    });
    setMonthYearRange((monthYearRange) => getMonthYearRange(range, monthYearRange));
  }, [range]);

  return {
    monthYearRange,
    setMonthYearRange,
    selectRange,
    handleMouseEnter,
    handleMouseLeave,
    handleSelect,
    isSelected,
    isBetween,
  };
};

export const useDRDPMonthYear = (index: keyof IYearRange) => {
  const { monthYearRange, setMonthYearRange } = useDRDPContext();

  const canPrevious =
    index === "startDate"
      ? true
      : compareAsc(sub(monthYearRange.endDate, { months: 1 }), monthYearRange.startDate) === 1;

  const canNext =
    index === "startDate"
      ? compareAsc(add(monthYearRange.startDate, { months: 1 }), monthYearRange.endDate) === -1
      : true;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const onPrevious = useCallback(() => {
    if (canPrevious) {
      setMonthYearRange((monthYearRange) => ({
        ...monthYearRange,
        [index]: sub(monthYearRange[index], { months: 1 }),
      }));
    }
  }, [index, canPrevious]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const onNext = useCallback(() => {
    if (canNext) {
      setMonthYearRange((monthYearRange) => ({
        ...monthYearRange,
        [index]: add(monthYearRange[index], { months: 1 }),
      }));
    }
  }, [index, canNext]);

  return { monthYear: monthYearRange[index], onPrevious, onNext, canPrevious, canNext };
};
